import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { ArrowLeft, Music } from 'lucide-react';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import MobileLoginPage from '@/features/shared/components/MobileLoginPage';
import AgentArtistInviteBand from '@/components/landing/AgentArtistInviteBand';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const { signIn } = useAuth();
  const mobile = useMobile();
  const timeoutRef = useRef(null);
  const [searchParams] = useSearchParams();
  const signupWithAgentHref = useMemo(() => {
    const a = searchParams.get('agent')?.trim();
    return a ? `/signup?agent=${encodeURIComponent(a.toUpperCase())}` : '/signup';
  }, [searchParams]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    try {
      // Set a maximum timeout for the entire sign-in process
      const signInPromise = signIn(email, password);
      const maxTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sign-in timeout')), 10000)
      );
      
      const { error } = await Promise.race([signInPromise, maxTimeout]);
      
      // If there's an error, reset loading state
      if (error) {
        setLoading(false);
        return;
      }
      
      // If successful, navigation will happen in signIn
      // Add a shorter timeout to reset loading state if navigation takes too long
      timeoutRef.current = setTimeout(() => {
        setLoading(false);
        // If still on login page after timeout, force navigation
        if (window.location.pathname.includes('/login')) {
          console.warn('⚠️ Navigation seems to have failed, redirecting manually');
          window.location.href = '/admin';
        }
      }, 3000); // Reset after 3 seconds max to prevent infinite loading
    } catch (err) {
      console.error('Login error:', err);
      setLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      alert('Please enter your email address first.');
      return;
    }

    setResetSending(true);
    try {
      // Delegate to auth context if it exposes a reset helper, otherwise use Firebase auth directly
      const { auth } = await import('@/lib/firebase');
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, trimmedEmail);
      alert('If an account exists for this email, a password reset link has been sent.');
    } catch (err) {
      console.error('Password reset error:', err);
      alert(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  // Return mobile version if on mobile device
  if (mobile.isMobile) {
    return <MobileLoginPage />;
  }

  return (
    <>
      <Helmet>
        <title>Artist Login - Your Song Request</title>
        <meta name="description" content="Login to your artist account and manage your song requests, setlist, and performances." />
        <meta property="og:title" content="Artist Login - Your Song Request" />
        <meta property="og:description" content="Login to your artist account and manage your song requests, setlist, and performances." />
      </Helmet>

      <div className="min-h-screen bg-black flex flex-col items-center justify-start px-4 py-8 sm:justify-center sm:py-10">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-cyan-900/10"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 w-full max-w-lg"
        >
          <AgentArtistInviteBand mode="login" />
          <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <Card className="glass-effect border-gray-800 mx-auto max-w-md w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Music className="w-12 h-12 text-blue-500" />
              </div>
              <CardTitle className="text-2xl font-bold text-white">Artist Login</CardTitle>
              <CardDescription className="text-gray-400">
                Access your artist dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-gray-900 border-gray-700 text-white"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-gray-900 border-gray-700 text-white"
                    placeholder="••••••••"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetSending || loading}
                    className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
                  >
                    {resetSending ? 'Sending reset link…' : 'Forgot your password?'}
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 neon-glow"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
              
              {/* Karaoke Mode Link */}
              <div className="mt-6 p-4 bg-purple-900/20 border border-purple-700/50 rounded-lg">
                <p className="text-purple-200 text-sm mb-2">
                  🎤 Looking for Karaoke Mode?
                </p>
                <Link 
                  to="/karaoke/login" 
                  className="inline-flex items-center text-purple-300 hover:text-purple-200 font-semibold text-sm transition-colors"
                >
                  Login HERE! 🎤
                </Link>
                <p className="text-purple-300 text-xs mt-1">
                  Perfect for DJs and Karaoke Hosts
                </p>
                <p className="text-purple-400 text-xs mt-2 italic">
                  If you're a karaoke host, you should use the karaoke login page instead!
                </p>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-gray-400">
                  Don't have an account?{' '}
                  <Link to={signupWithAgentHref} className="text-blue-400 hover:text-blue-300 font-semibold">
                    Sign up here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default LoginPage;